import type { Api_AdConfig } from '@core/api'
import type { Channel } from '@core/channel'
import type { FeedSourceConfig } from '@core/feed'
import type {
  AddToCalendarParams,
  EmbedFeedParams,
  IHerounitParams,
  IPresentation,
  IProgrammaticPlayerParams,
  IStoryblockParams,
  IStorylinkParams,
  ShareParams,
  WidgetSourceEnum,
} from '@core/types'
import type {
  AppContextType,
  Context,
  IAppContext,
  IAppContextHashtag,
} from '@embed/components/helpers'

export type IContextTrackingData =
  | {
      context: Context.CONTEXT_CHANNEL
      channel?: string
      channel_id?: string
    }
  | {
      context: Context.CONTEXT_PLAYLIST
      channel?: string
      channel_id?: string
      playlist_id?: string
    }
  | {
      context: Context.CONTEXT_PLAYLIST_COLLECTION
      channel?: string
      channel_id?: string
      playlist_id?: string
    }
  | {
      context: Context.CONTEXT_HASHTAG
      channel?: string
      channel_id?: string
      hashtag?: string
    }
  | {
      context: Context.CONTEXT_SKUS
      channel?: string
      channel_id?: string
      skus?: string
    }
  | {
      context: Context.CONTEXT_VIDEOS
      channel?: string
      channel_id?: string
      videos?: string
    }

export type IContextTrackingDataPixel =
  | {
      context: Context.CONTEXT_CHANNEL
      channel?: string
      _channel_id?: string
    }
  | {
      context: Context.CONTEXT_PLAYLIST
      channel?: string
      _channel_id?: string
      _playlist_id?: string
    }
  | {
      context: Context.CONTEXT_PLAYLIST_COLLECTION
      channel?: string
      _channel_id?: string
      _playlist_id?: string
    }
  | {
      context: Context.CONTEXT_HASHTAG
      hashtag?: string
      channel?: string
      _channel_id?: string
    }
  | {
      context: Context.CONTEXT_SKUS
      channel?: string
      _channel_id?: string
      skus?: string
    }
  | {
      context: Context.CONTEXT_VIDEOS
      channel?: string
      _channel_id?: string
      _videos?: string
    }

export interface IWidgetAppContext<TParams> {
  adConfig: Api_AdConfig | null | undefined
  appContextType: AppContextType
  channel?: Channel | null
  channelId?: string
  embedFeedConfig: FeedSourceConfig
  embedInstanceId: string
  guestId: string
  hashtag?: IAppContextHashtag
  pageUrl?: string
  params: TParams
  username?: string
  trackingData: IContextTrackingData & {
    app_context_type: AppContextType
    guest_id: string
    page_url: string
    presentation: IPresentation
    product: string
    embed_instance_id: string
    channel_id?: string
    channel?: string
  }
  trackingDataPixel: Partial<
    IContextTrackingDataPixel & {
      app_context_type: AppContextType
      presentation: IPresentation
      page_url: string
      product: string
      guest_id: string
      _referrer_channel_id?: string
      _referrer_video_id?: string
      referrer_origin?: string
      referrer_guest_id?: string
      widget_config_id?: string
      widget_source?: WidgetSourceEnum
      _business_id?: string
    }
  >
}

export type EmbedFeedAppContext = IWidgetAppContext<EmbedFeedParams>
export type StoryblockAppContext = IWidgetAppContext<IStoryblockParams>
export type HerounitAppContext = IWidgetAppContext<IHerounitParams>
export type AddToCalendarAppContext = IWidgetAppContext<AddToCalendarParams>
export type ShareAppContext = IWidgetAppContext<ShareParams>
export type StorylinkAppContext = IWidgetAppContext<IStorylinkParams>
export type ProgrammaticAppContext =
  IWidgetAppContext<IProgrammaticPlayerParams>

export type AnyWidgetAppContext =
  | EmbedFeedAppContext
  | StoryblockAppContext
  | HerounitAppContext
  | AddToCalendarAppContext
  | ShareAppContext
  | StorylinkAppContext

/**
 * Type guard to check if the given app context is any of the widget app contexts.
 */
export function isAnyWidgetAppContext(
  context: IAppContext | AnyWidgetAppContext | undefined,
): context is AnyWidgetAppContext {
  return (
    !!context && 'trackingData' in context && 'trackingDataPixel' in context
  )
}
